import React, { useEffect, useState } from "react";
import { useChatContext } from "./providers/ChatProvider";
import { ProjectBadge, type ProjectChoice } from "./ProjectBadge";
import { listProjects, moveSession } from "../../api/projects";
import { CreateProjectDialog } from "../Projects/CreateProjectDialog";

/**
 * Project selector rendered inside the composer header (new design). Reads
 * the current project from ChatContext and lets the user move the session
 * into / out of a project. Mirrors the old header-bar ProjectBadgeHeader but
 * lives in the composer so the model + project chips sit together above the
 * input. Renders nothing when the `projects` feature is off.
 */
export function ComposerProjectBadge() {
  const { sessionId, projectId, setProjectId, features } = useChatContext();
  const [availableProjects, setAvailableProjects] = useState<ProjectChoice[]>([]);
  const [createOpen, setCreateOpen] = useState(false);

  useEffect(() => {
    if (!features.projects) return;
    listProjects()
      .then((ps) => setAvailableProjects(ps.map((p) => ({ id: p.id, name: p.name }))))
      .catch(() => {/* degrade silently */});
  }, [features.projects]);

  if (!features.projects) return null;

  const handleMove = async (newProjectId: string | null) => {
    if (sessionId) {
      await moveSession(sessionId, newProjectId);
    }
    setProjectId(newProjectId);
  };

  const handleCreateProject = () => {
    setCreateOpen(true);
  };

  return (
    <>
      <ProjectBadge
        sessionId={sessionId ?? ""}
        currentProjectId={projectId}
        availableProjects={availableProjects}
        onMove={handleMove}
        onCreateProject={handleCreateProject}
      />
      <CreateProjectDialog
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        onCreated={async (project) => {
          setCreateOpen(false);
          setAvailableProjects((prev) =>
            prev.some((p) => p.id === project.id)
              ? prev
              : [...prev, { id: project.id, name: project.name }],
          );
          if (projectId !== project.id) {
            await handleMove(project.id);
          }
        }}
      />
    </>
  );
}
